import { useCallback, useEffect, useMemo, useState } from 'react';
import { relativeRangeWithin } from '../utils/financialYearWindow';

/**
 * The Time Range and Aggregation pair every graph card carries.
 *
 * The dashboard held four copies of this and the details page a fifth, each
 * with its own effects. They also resolved their own date range from
 * `moment()`, which is how the financial year filter came to match nothing on
 * a past year: the range read from today while the year went to the backend as
 * a separate filter. Every card now resolves its window through
 * `relativeRangeWithin`, which reads from the selected year.
 *
 * Picking one control adjusts the other, so a range never arrives with an
 * aggregation that would return two buckets or twenty thousand. That pairing
 * runs in the change handlers rather than in effects watching the state it
 * sets — the two effects it replaces needed a pair of refs between them purely
 * to stop each other looping.
 */

/** Aggregation that suits each range. */
const AGGREGATION_FOR_RANGE = {
  hour: 'minute',
  day: 'hour',
  week: 'day',
  month: 'day',
  thisMonth: 'day',
  thisQuarter: 'day',
  thisFY: 'month',
  custom: 'hour',
};

/** Range that suits each aggregation. */
const RANGE_FOR_AGGREGATION = {
  minute: 'hour',
  hour: 'day',
  day: 'week',
  month: 'month',
};

/** Delay before a filter change reaches the network. */
const DEBOUNCE_MS = 300;

/**
 * Holds a value back until it stops changing.
 *
 * Both controls can move from one interaction, and each move rebuilds the
 * request; without this the card fires a query for the intermediate state as
 * well as the one the reader asked for.
 *
 * @param {*} value - Value to delay.
 * @param {number} delay - Milliseconds of quiet before it is published.
 * @returns {*} The value, once it has settled.
 */
export const useDebouncedValue = (value, delay = DEBOUNCE_MS) => {
  const [settled, setSettled] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return settled;
};

/**
 * Filter state for one graph card.
 *
 * @param {object} options - Configuration.
 * @param {string} options.financialYear - Year the card is scoped to.
 * @param {string} options.initialRange - Range to open on.
 * @param {string} options.initialAggregation - Aggregation to open on.
 * @returns {object} The current selection, the window it resolves to, and the
 *   handlers that keep the two controls in step.
 */
export const useGraphFilters = ({
  financialYear,
  initialRange = 'week',
  initialAggregation = 'day',
}) => {
  const [timeRange, setTimeRange] = useState(initialRange);
  const [aggregation, setAggregation] = useState(initialAggregation);
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);

  const selectTimeRange = useCallback((value) => {
    setTimeRange(value);

    const paired = AGGREGATION_FOR_RANGE[value];
    if (paired) setAggregation(paired);
  }, []);

  const selectAggregation = useCallback((value) => {
    setAggregation(value);

    const paired = RANGE_FOR_AGGREGATION[value];
    if (paired) setTimeRange((current) => (current === 'custom' ? current : paired));
  }, []);

  const dateRange = useMemo(
    () =>
      relativeRangeWithin(timeRange, financialYear, {
        start: customStart,
        end: customEnd,
      }),
    [timeRange, financialYear, customStart, customEnd]
  );

  return {
    timeRange,
    aggregation,
    customStart,
    customEnd,
    dateRange,
    selectTimeRange,
    selectAggregation,
    setCustomStart,
    setCustomEnd,
  };
};
